import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Mentions } from '@kosmos/shared';
import { IEnvironnements } from "../components/environnement/environnements";

interface AppState {
  theme: string;
  sidebar:
    | { title: string; content: any }
    | undefined /* react component (show) or undefined (hide)*/;
  dialog:
    | { title: string; content: any, width?: string }
    | undefined /* react component (show) or undefined (hide)*/;
  toastRedux:
    | { title?: string; content: string; severity: 'success' | 'info' | 'warning' | 'error' | 'default'; life: number }
    | undefined;
  config: Configuration;
  requireAuthRedirect: boolean;
  authParams: Unauthorizedresponse;
}

export interface Unauthorizedresponse {
  oauth2_login_url: string;
  basic_auth_available: boolean;
  basic_auth_frontend_available: boolean;
  oauth2_auth_available: boolean;
}
export interface Profile {
  username: string | undefined;
  email: string | undefined;
  name: string | undefined;
  permissions: string[] | undefined;
  given_name?: string;
  family_name?: string;
}

export interface Configuration {
  environnements: IEnvironnements[];
  sasWarningMessage: string;
  runtimeMode: RuntimeMode[];
  defaultsKubernetesValues: DefaultsKubernetesValues | undefined;
  metricList: string[];
  cds: CdsConfig;
  realm: string;
  platformLevel: Mentions;
  onlyMyUdf: boolean;
  portailUrl: string;
  lastVersionName: string;
  datapipelineUiImage: string;
  treatmentVersion: string;
}

export interface DefaultsKubernetesValues {
    defaultMemoryRequest:string
    defaultCpuRequest:string
    defaultCpuLimit:string
    defaultMemoryLimit:string
    memoryMin:string
    cpuMin:string
    memoryMax:string
    cpuMax:string

}

export interface CdsConfig {
  isActive: boolean;
}

export interface RuntimeProperties {
  key: string;
  name: string;
  value: any;
  help: string;
  type: string;
}

export interface RuntimeMode {
  name: string;
  displayName: string;
  description: string[];
  icon: IIcon;
  properties: RuntimeProperties[];
}

export interface IIcon {
  type: string;
  color: string;
}

const initialState = {
  theme: localStorage.getItem("theme-color") || "light",
  config: {
    sasWarningMessage: "",
  },
  requireAuthRedirect: false,
} as AppState;

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setConfig(state, action: PayloadAction<Configuration>) {
      if (action.payload.environnements)
        state.config.environnements = action.payload.environnements;
      if (action.payload.sasWarningMessage)
        state.config.sasWarningMessage = action.payload.sasWarningMessage;
      if (action.payload.runtimeMode)
        state.config.runtimeMode = action.payload.runtimeMode;
      if (action.payload.defaultsKubernetesValues)
        state.config.defaultsKubernetesValues =
          action.payload.defaultsKubernetesValues;
      if (action.payload.cds) {
        state.config.cds = action.payload.cds;
      }
      if (action.payload.realm) {
        state.config.realm = action.payload.realm;
      }
      if (action.payload.platformLevel) {
        state.config.platformLevel = action.payload.platformLevel;
      }
      state.config.lastVersionName = action.payload.lastVersionName;
      if (action.payload.datapipelineUiImage) {
        state.config.datapipelineUiImage = action.payload.datapipelineUiImage;
      }
    },
    setTheme(state, action: PayloadAction<string>) {
      localStorage.setItem("theme-color", action.payload);
      state.theme = action.payload;
    },
    setSidebar(
      state,
      action: PayloadAction<{ title: string; content: any } | undefined>
    ) {
      state.sidebar = action.payload;
    },
    setDialog(
      state,
      action: PayloadAction<
        { title: string; content: any; width?: string } | undefined
      >
    ) {
      state.dialog = action.payload;
    },
    setToast(
      state,
      action: PayloadAction<
        | { title?: string; content: any; severity: 'success' | 'info' | 'warning' | 'error' | 'default'; life: number }
        | undefined
      >
    ) {
      state.toastRedux = action.payload;
    },
    setRequireAuthRedirect: (state, action: PayloadAction<boolean>) => {
      state.requireAuthRedirect = action.payload;
    },
    setAuthParams: (state, action: PayloadAction<Unauthorizedresponse>) => {
      state.authParams = action.payload;
    },
  },
});

export const { setRequireAuthRedirect, setAuthParams, setTheme, setSidebar, setDialog, setToast, setConfig } =
  appSlice.actions;

  export default appSlice.reducer;
